'use client';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import addDays from 'date-fns/addDays';
import isAfter from 'date-fns/isAfter';
import isToday from 'date-fns/isToday';
import { useEffect, useMemo } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import {
  useAccessToken,
  useSlug
} from 'reservations/account/providers/ConfigProvider';
import {
  useLocation,
  useReservationsConfig
} from 'reservations/account/providers/ConfigProvider';
import AllTimeSlots from 'reservations/availabilities/components/AllTimeSlots';
import ClosestAvailableTimeSlots from 'reservations/availabilities/components/ClosestAvailableTimeSlots';
import { NoAvailabilities } from 'reservations/availabilities/components/NoAvailabilities';
import { OutsideAvailabilityWindow } from 'reservations/availabilities/components/OutsideAvailabilityWindow';
import { ANYTIME } from 'reservations/availabilities/constants';
import { useAvailabilitiesContext } from 'reservations/availabilities/providers/AvailabilitiesProvider';
import { useAvailabilitiesQuery } from 'reservations/availabilities/queries/useAvailabilitiesQuery';
import type { Slot } from 'reservations/availabilities/types';
import LoadingSpinner from 'reservations/base/components/LoadingSpinner';
import SystemError from 'reservations/base/components/SystemError';
import { COMMON_DATETIME_FORMATS } from 'reservations/base/helpers/datetime';
import { ModalType, useModal } from 'reservations/base/providers/ModalProvider';
import { RESERVATION, WAITPARTY } from 'reservations/booking/types';
import { WHITE } from 'reservations/brand/constants';
import { useBookingTypes } from 'reservations/form/hooks/useBookingTypes';
import JoinWaitlistButton from 'reservations/waitlist/components/JoinWaitlistButton';
import { useWaitlist } from 'reservations/waitlist/providers/WaitlistProvider';

interface AvailabilitiesProps {
  numberOfGuests?: number;
}

function Availabilities({ numberOfGuests = 0 }: AvailabilitiesProps) {
  const { hasActiveOnlineWaitlist, waitCount } = useWaitlist();
  const { handleSetTimePickerSlot } = useAvailabilitiesContext();
  const { maxAdvanceDays } = useReservationsConfig();
  const { phone_number, email_address } = useLocation();

  const accessToken = useAccessToken();
  const slug = useSlug();
  const { open } = useModal();
  const {
    field: { onChange, name }
  } = useController({
    name: 'availability'
  });
  const { setValue } = useFormContext();

  const [partySize, date, time] = useWatch({
    name: ['partySize', 'date', 'time']
  });

  const formattedDateString = useMemo(
    () => COMMON_DATETIME_FORMATS.YMMdd(date),
    [date]
  );
  const { isModify } = useBookingTypes();
  const showJoinWaitlistButton = useMemo(
    () => isToday(date) && !isModify && hasActiveOnlineWaitlist,
    [date, hasActiveOnlineWaitlist, isModify]
  );

  const showJoinWaitlistButtonWithAvailabilities = useMemo(
    () => time === ANYTIME && showJoinWaitlistButton,
    [time, showJoinWaitlistButton]
  );

  const { data, isPending, isError } = useAvailabilitiesQuery({
    partySize,
    slug,
    apiKey: accessToken,
    date: formattedDateString
  });

  useEffect(() => {
    if (!data?.isEmpty) {
      const timePickerSlots = data?.slots
        .map((slot: Slot | null, index: number) => ({
          index,
          time: slot?.time12
        }))
        .filter(slot => slot?.time != null);

      if (timePickerSlots != null) {
        handleSetTimePickerSlot(timePickerSlots);
      }
    } else {
      handleSetTimePickerSlot([]);
    }
  }, [data?.isEmpty]);

  // istanbul ignore next
  const handleSlotClick = (timeSlot: string) => {
    setValue('bookingType', RESERVATION);
    onChange(timeSlot);

    const modal =
      isModify && numberOfGuests > partySize
        ? ModalType.ModifyGuests
        : ModalType.BookingFields;

    open(modal);
  };

  const handleWaitlistClick = () => {
    setValue('bookingType', WAITPARTY);
    open(ModalType.BookingFields);
  };

  if (isPending) {
    return (
      <Box sx={{ py: '16px' }}>
        <LoadingSpinner message="Loading Reservations..." />
      </Box>
    );
  }

  // istanbul ignore next
  if (isError) {
    return (
      <Box sx={{ p: '32px' }}>
        <SystemError />
      </Box>
    );
  }

  const todaysDate = new Date();
  const lastDayToMakeReservation = addDays(todaysDate, maxAdvanceDays);

  if (isAfter(date, lastDayToMakeReservation)) {
    return (
      <OutsideAvailabilityWindow
        date={date}
        maxAdvanceDays={maxAdvanceDays}
        lastDayToMakeReservation={lastDayToMakeReservation}
      />
    );
  }

  if (data.isEmpty && showJoinWaitlistButton) {
    return (
      <Grid container direction="column" gap="36px">
        <Grid item sx={{ px: '16px' }}>
          <Typography
            sx={{
              fontSize: '16px',
              textAlign: 'center'
            }}
          >
            We don&apos;t have any more reservations today, but we do hold
            tables for walk-ins.
          </Typography>
        </Grid>
        <Grid item sx={{ px: '16px' }}>
          <JoinWaitlistButton
            showNumberOfPartiesAhead
            sxProps={{ maxWidth: '95%' }}
            onClick={handleWaitlistClick}
          />
        </Grid>
      </Grid>
    );
  }

  if (data.isEmpty) {
    return (
      <NoAvailabilities
        date={date}
        partySize={partySize}
        phoneNumber={phone_number}
        emailAddress={email_address}
      />
    );
  }

  return (
    <Grid
      container
      direction="column"
      gap="24px"
      data-testid="availabilities-container"
    >
      {showJoinWaitlistButtonWithAvailabilities && (
        <Grid sx={{ px: '16px' }}>
          <Button onClick={handleWaitlistClick} size="fullWidth">
            <Box display="flex" flexDirection="column">
              <Typography
                fontSize="18px"
                color={WHITE}
                fontWeight={700}
                sx={{ lineHeight: 'initial' }}
              >
                Join Waitlist
              </Typography>
              <Typography
                fontSize="14px"
                color={WHITE}
                fontWeight={300}
                sx={{ lineHeight: 'initial' }}
              >{`${waitCount} part${waitCount === 1 ? 'y' : 'ies'} on the list`}</Typography>
            </Box>
          </Button>
        </Grid>
      )}
      <Grid
        item
        container
        alignContent="center"
        justifyContent="center"
        spacing="4px"
        sx={{ px: '16px' }}
      >
        {time === ANYTIME ? (
          <AllTimeSlots
            slots={data.slots}
            handleSlotClick={handleSlotClick}
            name={name}
          />
        ) : (
          <ClosestAvailableTimeSlots
            slots={data.slots}
            handleSlotClick={handleSlotClick}
            time={time}
            name={name}
          />
        )}
      </Grid>
    </Grid>
  );
}

export default Availabilities;
